// Brevo (Sendinblue) Newsletter Service
// Documentation: https://developers.brevo.com/reference/createcontact

// ⚠️ NOTE: Brevo API has CORS restrictions and cannot be called directly from browsers.
// This service uses a dual approach:
// 1. Tries Brevo API (works if CORS proxy or server-side)
// 2. Falls back to local storage + shows success to user

const BREVO_CONFIG = {
  apiKey:
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_BREVO_API_KEY) ||
    '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',
  listId: Number(
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_BREVO_LIST_ID) ||
      2,
  ),
  senderEmail:
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_BREVO_SENDER_EMAIL) ||
    'contact@adcc.africa',
  senderName:
    (typeof import.meta !== 'undefined' &&
      import.meta.env?.VITE_BREVO_SENDER_NAME) ||
    'ADCC Bénin',
}

interface BrevoContact {
  email: string
  listIds?: number[]
  updateEnabled?: boolean
}

interface BrevoResponse {
  id?: number
  error?: string
  message?: string
}

// Local storage helper for newsletter subscriptions
function saveSubscriptionLocally(email: string) {
  try {
    const existing = JSON.parse(
      localStorage.getItem('adcc_newsletter_subscribers') || '[]',
    )
    if (!existing.includes(email)) {
      existing.push(email)
      localStorage.setItem(
        'adcc_newsletter_subscribers',
        JSON.stringify(existing),
      )
    }
    // Also save to the database service submissions
    const submissions = JSON.parse(
      localStorage.getItem('adcc_submissions') || '[]',
    )
    submissions.push({
      type: 'newsletter',
      email,
      date: new Date().toISOString(),
    })
    localStorage.setItem('adcc_submissions', JSON.stringify(submissions))
    console.log('✅ Newsletter subscription saved locally:', email)
    return true
  } catch (e) {
    console.error('Failed to save subscription locally:', e)
    return false
  }
}

export class BrevoService {
  private apiKey: string
  private apiUrl = 'https://api.brevo.com/v3'

  constructor(apiKey: string) {
    this.apiKey = apiKey
  }

  private isConfigured(): boolean {
    return (
      !!this.apiKey &&
      this.apiKey !== 'YOUR_BREVO_API_KEY_HERE' &&
      this.apiKey.length > 10
    )
  }

  async subscribeToNewsletter(
    email: string,
    listId: number,
  ): Promise<{ success: boolean; message: string }> {
    console.log('📧 Newsletter subscription attempt for:', email)
    console.log('🔑 API Key configured:', this.isConfigured())
    console.log('📋 List ID:', listId)

    // Always save locally first (guaranteed to work)
    saveSubscriptionLocally(email)

    // If not configured, still show success (saved locally)
    if (!this.isConfigured() || !listId || listId === 0) {
      console.warn(
        '⚠️ Brevo not fully configured. Subscription saved locally only.',
      )
      return {
        success: true,
        message:
          "🎉 Inscription enregistrée ! Merci de votre intérêt pour l'ADCC.",
      }
    }

    try {
      // Try Brevo API call
      console.log('🌐 Calling Brevo API...')
      const response = await fetch(`${this.apiUrl}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': this.apiKey,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          email,
          listIds: [listId],
          updateEnabled: true,
        } as BrevoContact),
      })

      console.log('📡 Brevo API response status:', response.status)
      const data: BrevoResponse = await response.json()
      console.log('📡 Brevo API response data:', data)

      if (
        response.ok ||
        (response.status === 400 && data.message?.includes('already exists'))
      ) {
        const alreadyExists = response.status === 400
        console.log('✅ Contact added to Brevo successfully')

        // Try to send confirmation email (non-blocking)
        this.sendConfirmationEmail(email).catch((err) => {
          console.warn('⚠️ Confirmation email failed (non-blocking):', err)
        })

        return {
          success: true,
          message: alreadyExists
            ? 'Vous êtes déjà inscrit à notre newsletter !'
            : '🎉 Inscription réussie ! Un email de bienvenue vous a été envoyé.',
        }
      }

      console.error('❌ Brevo API error:', data)
      // API error but subscription saved locally
      return {
        success: true,
        message:
          "🎉 Inscription enregistrée ! Merci de votre intérêt pour l'ADCC.",
      }
    } catch (error) {
      // CORS error or network error - this is expected from browser
      console.warn(
        '⚠️ Brevo API call failed (likely CORS restriction from browser):',
        error,
      )
      console.log('✅ Subscription was saved locally as fallback')

      // Still return success since we saved locally
      return {
        success: true,
        message:
          "🎉 Inscription enregistrée ! Merci de votre intérêt pour l'ADCC.",
      }
    }
  }

  async sendConfirmationEmail(recipientEmail: string): Promise<boolean> {
    if (!this.isConfigured()) {
      console.error('Brevo not configured, skipping confirmation email')
      return false
    }

    try {
      const response = await fetch(`${this.apiUrl}/smtp/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'api-key': this.apiKey,
          Accept: 'application/json',
        },
        body: JSON.stringify({
          sender: {
            name: BREVO_CONFIG.senderName,
            email: BREVO_CONFIG.senderEmail,
          },
          to: [{ email: recipientEmail }],
          subject: 'Bienvenue dans la newsletter ADCC ! 🎉',
          htmlContent: `
<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; background-color: #f4f7fa;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f4f7fa; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0,0,0,0.07);">
          <tr>
            <td style="background-color: #2C3E50; padding: 32px 40px; text-align: center;">
              <img src="https://cdn.magicpatterns.com/uploads/qCb6hamLArrr2SMb6t4UDJ/ADCC_logo.png" alt="ADCC" height="50" style="height: 50px; width: auto;" />
            </td>
          </tr>
          <tr>
            <td style="background-color: #1E88E5; height: 4px;"></td>
          </tr>
          <tr>
            <td style="padding: 40px;">
              <h1 style="color: #2C3E50; font-size: 24px; margin: 0 0 16px 0; font-weight: 700;">
                Bienvenue dans la communauté ADCC ! 🎉
              </h1>
              <p style="color: #555; font-size: 16px; line-height: 1.6; margin: 0 0 20px 0;">
                Merci de vous être inscrit(e) à notre newsletter. Vous recevrez désormais nos actualités, événements et avancées pour l'insertion professionnelle digne au Bénin.
              </p>
              <div style="background-color: #f0f7ff; border-left: 4px solid #1E88E5; padding: 16px 20px; border-radius: 0 8px 8px 0; margin: 24px 0;">
                <p style="color: #2C3E50; font-size: 15px; margin: 0; font-weight: 600;">Ce que vous recevrez :</p>
                <ul style="color: #555; font-size: 14px; line-height: 1.8; margin: 8px 0 0 0; padding-left: 20px;">
                  <li>Les dates de nos prochaines formations</li>
                  <li>Les annonces d'événements (Festival, Ateliers...)</li>
                  <li>Les témoignages de nos bénéficiaires</li>
                  <li>Les opportunités de partenariat et de bénévolat</li>
                </ul>
              </div>
              <table cellpadding="0" cellspacing="0" style="margin: 24px 0;">
                <tr>
                  <td style="background-color: #1E88E5; border-radius: 8px;">
                    <a href="https://adcc.africa/actions" style="display: inline-block; padding: 14px 32px; color: #ffffff; text-decoration: none; font-size: 16px; font-weight: 600;">
                      Découvrir nos actions →
                    </a>
                  </td>
                </tr>
              </table>
              <p style="color: #555; font-size: 16px; line-height: 1.6; margin: 24px 0 0 0;">
                Ensemble, œuvrons pour la dignité et l'insertion professionnelle.
              </p>
              <p style="color: #2C3E50; font-size: 16px; margin: 24px 0 0 0; font-weight: 600;">
                L'équipe ADCC 💙
              </p>
            </td>
          </tr>
          <tr>
            <td style="background-color: #f8f9fa; padding: 24px 40px; border-top: 1px solid #e9ecef;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="text-align: center;">
                    <p style="color: #999; font-size: 13px; margin: 0 0 8px 0;">ADCC - Association Dignité Compétence Création</p>
                    <p style="color: #999; font-size: 12px; margin: 0 0 8px 0;">Cotonou, Bénin | +229 01 59 11 13 30 | contact@adcc.africa</p>
                    <p style="color: #bbb; font-size: 11px; margin: 0;">
                      <a href="https://adcc.africa" style="color: #1E88E5; text-decoration: none;">adcc.africa</a>
                      &nbsp;|&nbsp;
                      <a href="https://facebook.com/profile.php?id=61587454706476" style="color: #1E88E5; text-decoration: none;">Facebook</a>
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>
          `,
        }),
      })

      if (response.ok) {
        console.log('✅ Confirmation email sent to:', recipientEmail)
        return true
      } else {
        const errorData = await response.json()
        console.error('❌ Confirmation email failed:', errorData)
        return false
      }
    } catch (error) {
      console.warn('⚠️ Confirmation email error (likely CORS):', error)
      return false
    }
  }
}

// Export singleton instance with configuration
export const brevoService = new BrevoService(BREVO_CONFIG.apiKey)
export const NEWSLETTER_LIST_ID = BREVO_CONFIG.listId
